import useObservableValue from "../../../hooks/useObservableValue";
import filterState$, {
  filterCount$,
  initialFilterState,
} from "../observables/filterState$";
import { FILTER_STATE } from "../types/CommonTypes";
import { getAuthValue } from "../../authModule/hooks/useAuthValue";
import { USER_ROLES } from "../../userModule/types/CommonTypes";

const getSurgeonSelfName = (): string | null => {
  const { user } = getAuthValue();
  if (user?.role !== USER_ROLES.SURGEON) return null;
  const name = `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim();
  return name || null;
};

function useFilterValue(): FILTER_STATE {
  return useObservableValue(filterState$);
}

export function useFilterCount(): number {
  return useObservableValue(filterCount$);
}

export default useFilterValue;

export const getFilterValue = () => filterState$.getValue();

const sanitizeFilterState = (
  incoming: Partial<FILTER_STATE> | FILTER_STATE
): FILTER_STATE => {
  const allowedKeys = Object.keys(initialFilterState);
  const current = filterState$.getValue() as FILTER_STATE;
  const next: any = { ...current };

  allowedKeys.forEach((key) => {
    if (Object.hasOwn(incoming as object, key)) {
      const val: any = (incoming as any)[key];
      if (key === "dateRange") {
        const from = val?.from ?? null;
        const to = val?.to ?? null;
        next.dateRange = { from, to };
      } else {
        next[key] = Array.isArray(val) ? [...val] : val;
      }
    }
  });

  const surgeonSelfName = getSurgeonSelfName();
  const hasAttendingSurgeon =
    Array.isArray(next.attendingSurgeonNames) &&
    next.attendingSurgeonNames.length > 0;
  if (surgeonSelfName && !hasAttendingSurgeon) {
    next.attendingSurgeonNames = [surgeonSelfName];
  }

  return next as FILTER_STATE;
};

const applyFilterState = (incoming: Partial<FILTER_STATE> | FILTER_STATE) => {
  const next = sanitizeFilterState(incoming);
  filterState$.next(next);
  updateFilterCount(next);
};

export const setFilterValue = (filterState: FILTER_STATE) => {
  applyFilterState(filterState);
};

export const updateFilterValue = (filterState: Partial<FILTER_STATE>) => {
  applyFilterState(filterState);
};

export const resetFilterValue = () => {
  setFilterValue(initialFilterState);
};

export const updateFilterCount = (filters: FILTER_STATE) => {
  let count = 0;
  Object.entries(filters).forEach(([key, value]) => {
    if (["otsKeys", "procedureKeys", "nurseKeys"].includes(key)) return;

    if (Array.isArray(value) && value.length > 0) count++;
    if (value && typeof value === "string") count++;
  });

  if (filters?.dateRange?.from && filters?.dateRange?.to) count++;

  filterCount$.next(count);
};
